// Аналитика склада: все движения за период — приход, продажи по тех-картам,
// списания кассиров и админки, отходы заготовок, инвентаризация — в рублях,
// оборотная ведомость по позициям и журнал с источником каждой операции.
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Calendar, FileDown, Search, X } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import {
  getStockAnalytics, listStockMovements,
  STOCK_SOURCE_LABELS,
  type StockAnalytics, type StockMovement, type StockMovementSource, type StockMovementType,
} from '@/services/admin';
import { formatDate, formatRub } from '@/utils/format';
import { formatQuantity, trimNumber } from '@/utils/units';

type Preset = 'today' | '7d' | '30d' | 'month';
const PRESETS: Array<{ id: Preset; label: string }> = [
  { id: 'today', label: 'Сегодня' },
  { id: '7d', label: '7 дней' },
  { id: '30d', label: '30 дней' },
  { id: 'month', label: 'Месяц' },
];

const dateKey = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

function presetRange(p: Preset): { from: string; to: string } {
  const now = new Date();
  const to = dateKey(now);
  if (p === 'today') return { from: to, to };
  if (p === 'month') return { from: dateKey(new Date(now.getFullYear(), now.getMonth(), 1)), to };
  const start = new Date(now);
  start.setDate(start.getDate() - (p === '7d' ? 6 : 29));
  return { from: dateKey(start), to };
}

const movementLabels: Record<StockMovementType, string> = {
  ARRIVAL: 'Приход',
  CONSUMPTION: 'Расход',
  WRITE_OFF: 'Списание',
  INVENTORY: 'Инвентаризация',
  SALE: 'Продажа',
};
const movementTone: Record<StockMovementType, 'success' | 'warning' | 'danger' | 'info' | 'slate'> = {
  ARRIVAL: 'success',
  CONSUMPTION: 'warning',
  WRITE_OFF: 'danger',
  INVENTORY: 'info',
  SALE: 'slate',
};

const SOURCES: StockMovementSource[] = ['SALE', 'SUPPLY', 'KASSA', 'ADMIN', 'INVENTORY', 'RETURN'];

/**
 * Что показать в «Комментарии». У продаж, возвратов и инвентаризации текст
 * сгенерирован системой и повторяет подпись источника — его прячем. Старый бэк
 * (без source) отдаёт только comment.
 */
function noteOf(m: StockMovement): string {
  if (!m.source) return m.comment ?? '';
  if (m.source === 'SALE' || m.source === 'RETURN' || m.source === 'INVENTORY') return '';
  return m.note ?? '';
}

/** Количество со знаком: «−150 г», «+2 кг». */
function signedQty(value: number, unit: string): string {
  if (value === 0) return `0 ${unit}`;
  return `${value > 0 ? '+' : '−'}${formatQuantity(Math.abs(value), unit)}`;
}

function downloadCsv(name: string, rows: string[][]) {
  const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  // Safari обрывает скачивание, если отозвать ссылку сразу после click().
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

const shortDay = (key: string) => {
  const [, m, d] = key.split('-');
  return `${d}.${m}`;
};

export function StockAnalyticsTab() {
  const [preset, setPreset] = useState<Preset>('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const useCustom = Boolean(customFrom && customTo);
  const range = useCustom ? { from: customFrom, to: customTo } : presetRange(preset);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin/stock/analytics', range.from, range.to],
    queryFn: () => getStockAnalytics(range),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className={`flex gap-1 p-1 bg-slate-100 rounded-lg overflow-x-auto no-scrollbar ${useCustom ? 'opacity-50' : ''}`}>
          {PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => { setPreset(p.id); setCustomFrom(''); setCustomTo(''); }}
              className={`px-3 py-1.5 text-sm rounded-md whitespace-nowrap shrink-0 ${
                !useCustom && preset === p.id ? 'bg-white shadow text-brand-700' : 'text-slate-600'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-2 py-1">
          <Calendar size={15} className="text-slate-400" />
          <input
            type="date"
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
            className="text-sm outline-none bg-transparent"
            max={customTo || undefined}
          />
          <span className="text-slate-400 text-sm">—</span>
          <input
            type="date"
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
            className="text-sm outline-none bg-transparent"
            min={customFrom || undefined}
          />
          {(customFrom || customTo) && (
            <button
              onClick={() => { setCustomFrom(''); setCustomTo(''); }}
              className="text-slate-400 hover:text-slate-700"
              title="Сбросить"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {isLoading ? <Spinner label="Считаем движения склада" /> : isError || !data ? (
        <div className="card p-8 text-center text-sm text-slate-500">Не удалось загрузить аналитику склада</div>
      ) : (
        <>
          <SummaryGrid data={data} />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <DailyChart data={data} />
            <ReasonsCard data={data} />
          </div>
          <TurnoverCard data={data} />
        </>
      )}

      <MovementJournal from={range.from} to={range.to} />
    </div>
  );
}

// ─── Сводка ──────────────────────────────────────────────────────────────

function Kpi({ title, value, hint, tone = 'slate' }: {
  title: string;
  value: string;
  hint?: string;
  tone?: 'slate' | 'emerald' | 'rose' | 'amber';
}) {
  const accent = {
    slate: 'text-slate-900',
    emerald: 'text-emerald-700',
    rose: 'text-rose-700',
    amber: 'text-amber-700',
  }[tone];
  return (
    <div className="card p-4 min-w-0">
      <div className="text-xs text-slate-500 mb-1 truncate">{title}</div>
      <div className={`text-xl font-semibold tabular-nums ${accent}`}>{value}</div>
      {hint && <div className="text-xs text-slate-400 mt-1 truncate" title={hint}>{hint}</div>}
    </div>
  );
}

function SummaryGrid({ data }: { data: StockAnalytics }) {
  const s = data.summary;
  const netSales = s.sales - s.returns;
  const inventoryNet = s.inventorySurplus - s.inventoryShortage;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
      <Kpi title="Приход" value={formatRub(s.arrival)} hint="Поставки и ручной приход" tone="emerald" />
      <Kpi
        title="Продажи по тех-картам"
        value={formatRub(netSales)}
        hint={s.returns > 0 ? `Возвраты при отмене −${formatRub(s.returns)}` : 'Себестоимость проданного'}
      />
      <Kpi
        title="Списания"
        value={formatRub(s.writeOff)}
        hint={`Кассиры ${formatRub(s.writeOffKassa)} · вручную ${formatRub(s.writeOffAdmin)}`}
        tone={s.writeOff > 0 ? 'rose' : 'slate'}
      />
      <Kpi
        title="Потери на отходах"
        value={formatRub(s.waste)}
        hint="Отход заготовок при продаже"
        tone={s.waste > 0 ? 'amber' : 'slate'}
      />
      <Kpi
        title="Инвентаризация"
        value={`${inventoryNet > 0 ? '+' : inventoryNet < 0 ? '−' : ''}${formatRub(Math.abs(inventoryNet))}`}
        hint={`Излишки ${formatRub(s.inventorySurplus)} · недостача ${formatRub(s.inventoryShortage)}`}
        tone={inventoryNet < 0 ? 'rose' : inventoryNet > 0 ? 'emerald' : 'slate'}
      />
      <Kpi title="Ручной расход" value={formatRub(s.consumption)} hint={`Операций за период: ${s.movements}`} />
    </div>
  );
}

// ─── График по дням ──────────────────────────────────────────────────────

function DailyChart({ data }: { data: StockAnalytics }) {
  const rows = data.byDay.map((d) => ({
    date: d.date,
    in: d.inCost / 100,
    sales: d.salesCost / 100,
    writeOff: d.writeOffCost / 100,
    other: Math.max(0, d.outCost - d.salesCost - d.writeOffCost) / 100,
  }));
  const empty = data.summary.movements === 0;
  return (
    <div className="card p-5 lg:col-span-2">
      <div className="mb-3">
        <div className="font-medium">Движения по дням</div>
        <div className="text-xs text-slate-500">Приход и расход склада по себестоимости, ₽</div>
      </div>
      {empty ? (
        <div className="h-64 grid place-items-center text-sm text-slate-400">За период движений нет</div>
      ) : (
        <div className="h-64">
          <ResponsiveContainer>
            <BarChart data={rows}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tickFormatter={shortDay} tick={{ fontSize: 11 }} />
              <YAxis
                tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 1000)}к` : String(v))}
                tick={{ fontSize: 11 }}
              />
              <Tooltip
                formatter={(v: number) => `${v.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽`}
                labelFormatter={(l) => shortDay(String(l))}
              />
              <Legend
                formatter={(v) =>
                  v === 'in' ? 'Приход' : v === 'sales' ? 'Продажи' : v === 'writeOff' ? 'Списания' : 'Прочий расход'
                }
              />
              <Bar dataKey="in" fill="#10b981" radius={[3, 3, 0, 0]} />
              <Bar dataKey="sales" stackId="out" fill="#64748b" />
              <Bar dataKey="writeOff" stackId="out" fill="#f43f5e" />
              <Bar dataKey="other" stackId="out" fill="#f59e0b" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// ─── Причины списаний ────────────────────────────────────────────────────

function ReasonsCard({ data }: { data: StockAnalytics }) {
  const total = data.writeOffReasons.reduce((s, r) => s + r.cost, 0);
  const top = [...data.byItem].filter((i) => i.writeOffCost > 0)
    .sort((a, b) => b.writeOffCost - a.writeOffCost)
    .slice(0, 5);
  return (
    <div className="card p-5">
      <div className="font-medium mb-3">Списания</div>
      {data.writeOffReasons.length === 0 ? (
        <div className="text-sm text-slate-400">За период ничего не списывали</div>
      ) : (
        <div className="grid gap-2">
          {data.writeOffReasons.map((r) => {
            const share = total > 0 ? Math.round((r.cost / total) * 100) : 0;
            return (
              <div key={r.reason}>
                <div className="flex justify-between gap-2 text-sm">
                  <span className="text-slate-600 truncate">{r.label}</span>
                  <span className="tabular-nums font-medium">{formatRub(r.cost)}</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
                  <div className="h-full bg-rose-400 rounded-full" style={{ width: `${share}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
      {top.length > 0 && (
        <div className="mt-4 pt-3 border-t border-slate-100">
          <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Чаще всего списывают</div>
          <div className="grid gap-1">
            {top.map((i) => (
              <div key={i.id} className="flex justify-between gap-2 text-sm">
                <span className="truncate text-slate-600">{i.name}</span>
                <span className="tabular-nums shrink-0">
                  {formatQuantity(i.writeOff, i.unit)}
                  <span className="text-slate-400 ml-2">{formatRub(i.writeOffCost)}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Оборотная ведомость ─────────────────────────────────────────────────

function TurnoverCard({ data }: { data: StockAnalytics }) {
  const [search, setSearch] = useState('');
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? data.byItem.filter((i) => i.name.toLowerCase().includes(q)) : data.byItem;
  }, [data.byItem, search]);

  const exportCsv = () => {
    downloadCsv(`stock-turnover-${data.from}_${data.to}.csv`, [
      ['Позиция', 'Ед.', 'На начало', 'Приход', 'Возвраты', 'Продажи', 'Списания', 'Расход', 'Инвентаризация', 'На конец', 'Ушло, ₽', 'Списано, ₽', 'Отход, ₽'],
      ...rows.map((i) => [
        i.name, i.unit,
        trimNumber(i.opening), trimNumber(i.arrival), trimNumber(i.returns), trimNumber(i.sales),
        trimNumber(i.writeOff), trimNumber(i.consumption), trimNumber(i.inventory), trimNumber(i.closing),
        (i.outCost / 100).toFixed(2), (i.writeOffCost / 100).toFixed(2), (i.wasteCost / 100).toFixed(2),
      ]),
    ]);
  };

  return (
    <div className="card overflow-hidden">
      <div className="p-4 flex flex-wrap items-center justify-between gap-3 border-b border-slate-100">
        <div>
          <div className="font-medium">Оборотная ведомость</div>
          <div className="text-xs text-slate-500">
            Остаток на начало и конец периода, куда ушёл товар. Только позиции с движениями.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="input pl-9 w-56"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Найти позицию"
            />
          </div>
          <button className="btn-outline" onClick={exportCsv} disabled={rows.length === 0}>
            <FileDown size={16} /> CSV
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[960px] text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
              <th className="text-left px-4 py-3">Позиция</th>
              <th className="text-right px-3 py-3">На начало</th>
              <th className="text-right px-3 py-3">Приход</th>
              <th className="text-right px-3 py-3">Продажи</th>
              <th className="text-right px-3 py-3">Списания</th>
              <th className="text-right px-3 py-3">Расход</th>
              <th className="text-right px-3 py-3">Инвент.</th>
              <th className="text-right px-3 py-3">На конец</th>
              <th className="text-right px-4 py-3">Ушло, ₽</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((i) => (
              <tr key={i.id} className="table-row">
                <td className="px-4 py-2.5">
                  <div className="font-medium">{i.name}</div>
                  {i.wasteCost > 0 && (
                    <div className="text-xs text-amber-600">в т.ч. отход {formatRub(i.wasteCost)}</div>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-slate-500">{formatQuantity(i.opening, i.unit)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-emerald-700">
                  {i.arrival + i.returns > 0 ? formatQuantity(i.arrival + i.returns, i.unit) : '—'}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">{i.sales > 0 ? formatQuantity(i.sales, i.unit) : '—'}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-rose-600">{i.writeOff > 0 ? formatQuantity(i.writeOff, i.unit) : '—'}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{i.consumption > 0 ? formatQuantity(i.consumption, i.unit) : '—'}</td>
                <td className={`px-3 py-2.5 text-right tabular-nums ${i.inventory < 0 ? 'text-rose-600' : i.inventory > 0 ? 'text-emerald-700' : ''}`}>
                  {i.inventory !== 0 ? signedQty(i.inventory, i.unit) : '—'}
                </td>
                <td className={`px-3 py-2.5 text-right tabular-nums font-medium ${i.closing < 0 ? 'text-rose-600' : ''}`}>
                  {i.closing < 0 ? signedQty(i.closing, i.unit) : formatQuantity(i.closing, i.unit)}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums">{formatRub(i.outCost)}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={9} className="text-center text-slate-400 py-8">Нет движений за период</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Журнал движений ─────────────────────────────────────────────────────

const PAGE = 100;

function MovementJournal({ from, to }: { from: string; to: string }) {
  const [source, setSource] = useState<StockMovementSource | ''>('');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(PAGE);

  // Поиск идёт на сервер: без паузы каждая буква — отдельный запрос.
  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['admin/stock/movements', 'journal', from, to, source, query, limit],
    queryFn: () =>
      listStockMovements({
        dateFrom: from,
        dateTo: to,
        source: source || undefined,
        q: query || undefined,
        limit,
      }),
    placeholderData: (prev) => prev,
  });
  const rows = data ?? [];
  const canLoadMore = rows.length >= limit && limit < 1000;

  const exportCsv = () => {
    downloadCsv(`stock-movements-${from}_${to}.csv`, [
      ['Дата', 'Позиция', 'Операция', 'Источник', 'Количество', 'Ед.', 'Цена/ед., ₽', 'Сумма, ₽', 'Причина', 'Комментарий'],
      ...rows.map((m: StockMovement) => [
        new Date(m.createdAt).toLocaleString('ru-RU'),
        m.stockItem.name,
        movementLabels[m.type],
        m.sourceLabel ?? '',
        String(m.quantity),
        m.stockItem.unit,
        (m.costPerUnit / 100).toFixed(2),
        ((m.quantity * m.costPerUnit) / 100).toFixed(2),
        m.reasonLabel ?? '',
        m.note ?? m.comment ?? '',
      ]),
    ]);
  };

  return (
    <div className="card overflow-hidden">
      <div className="p-4 border-b border-slate-100 grid gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-medium">Журнал движений</div>
            <div className="text-xs text-slate-500">Каждая операция со складом за период и кто её провёл</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                className="input pl-9 w-56"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setLimit(PAGE); }}
                placeholder="Позиция"
              />
            </div>
            <button className="btn-outline" onClick={exportCsv} disabled={rows.length === 0}>
              <FileDown size={16} /> CSV
            </button>
          </div>
        </div>
        <div className="flex gap-1 flex-wrap">
          <SourceChip active={source === ''} onClick={() => { setSource(''); setLimit(PAGE); }}>Все</SourceChip>
          {SOURCES.map((s) => (
            <SourceChip key={s} active={source === s} onClick={() => { setSource(s); setLimit(PAGE); }}>
              {STOCK_SOURCE_LABELS[s]}
            </SourceChip>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        {isLoading ? <Spinner /> : (
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                <th className="text-left px-4 py-3">Дата</th>
                <th className="text-left px-4 py-3">Позиция</th>
                <th className="text-left px-4 py-3">Операция</th>
                <th className="text-right px-4 py-3">Кол-во</th>
                <th className="text-right px-4 py-3">Сумма</th>
                <th className="text-left px-4 py-3">Комментарий</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => {
                const text = noteOf(m);
                const waste = m.wasteQuantity != null && m.wasteQuantity > 0 ? m.wasteQuantity : 0;
                return (
                  <tr key={m.id} className="table-row align-top">
                    <td className="px-4 py-2.5 whitespace-nowrap text-slate-600">{formatDate(m.createdAt)}</td>
                    <td className="px-4 py-2.5 font-medium">{m.stockItem.name}</td>
                    <td className="px-4 py-2.5">
                      <Badge tone={movementTone[m.type]}>{movementLabels[m.type]}</Badge>
                      {m.sourceLabel && <div className="text-xs text-slate-500 mt-1">{m.sourceLabel}</div>}
                    </td>
                    <td className={`px-4 py-2.5 text-right tabular-nums font-medium whitespace-nowrap ${m.quantity < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>
                      {signedQty(m.quantity, m.stockItem.unit)}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">
                      {formatRub(Math.round(Math.abs(m.quantity) * m.costPerUnit))}
                    </td>
                    <td className="px-4 py-2.5 text-slate-500 max-w-[320px]">
                      {m.reasonLabel ? <span className="text-slate-700">{m.reasonLabel}</span> : null}
                      {m.reasonLabel && text ? ' — ' : null}
                      {text}
                      {waste > 0 ? (
                        <div className="text-xs text-amber-600">
                          в т.ч. отход {formatQuantity(waste, m.stockItem.unit)}
                        </div>
                      ) : null}
                      {!m.reasonLabel && !text && waste === 0 ? '—' : null}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="text-center text-slate-400 py-8">Нет движений за период</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      {canLoadMore && (
        <div className="p-3 border-t border-slate-100 text-center">
          <button className="btn-ghost text-sm" onClick={() => setLimit((l) => Math.min(1000, l + PAGE))} disabled={isFetching}>
            {isFetching ? 'Загружаем…' : 'Показать ещё'}
          </button>
        </div>
      )}
    </div>
  );
}

function SourceChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1 rounded-full text-xs border transition-colors ${
        active ? 'bg-brand-600 text-white border-brand-600' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
      }`}
    >
      {children}
    </button>
  );
}
